import { useEffect, useState } from 'react'
import { parseConfig } from './types'
import type { ComponentType } from 'react'
import type { CustomFieldDefinitionDocument, CustomFieldOption, CustomFieldType } from './types'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'

/**
 * Props shared by all field editor components (task detail view).
 */
export interface FieldEditorProps {
  definition: CustomFieldDefinitionDocument
  value: string | number | null
  options: Array<CustomFieldOption>
  onChange: (value: string | number | null) => void
  readOnly?: boolean
  compact?: boolean
}

const FULL_EDITOR_CLASSES =
  'h-8 w-full rounded-md border border-input bg-background px-2 text-base md:text-sm'
const COMPACT_EDITOR_CLASSES =
  'h-auto w-full border-0 bg-transparent p-0 text-base shadow-none focus:outline-none focus:ring-0 md:text-sm'

/**
 * Props for compact cell renderers (task list columns).
 */
export interface FieldCellProps {
  definition: CustomFieldDefinitionDocument
  value: string | number | null
  options: Array<CustomFieldOption>
}

/**
 * Registry entry for a field type. All consuming components look up
 * the registry by fieldType - they never switch on individual definitions.
 */
export interface FieldTypeRegistryEntry {
  Editor: ComponentType<FieldEditorProps>
  CellRenderer: ComponentType<FieldCellProps>
  formatValue: (value: string | number | null, definition: CustomFieldDefinitionDocument, options: Array<CustomFieldOption>) => string
  validateValue: (value: string | number | null, config: any) => string | null
}

// Lazy-loaded editors to avoid importing all at once
const StringFieldEditor = ({ value, onChange, definition, compact, readOnly }: FieldEditorProps) => {
  const config = parseConfig(definition.config)
  const committedValue = value != null ? String(value) : ''
  const [draft, setDraft] = useState(committedValue)

  useEffect(() => {
    setDraft(committedValue)
  }, [committedValue])

  const commit = () => {
    if (draft === committedValue) return
    onChange(draft === '' ? null : draft)
  }

  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          setDraft(committedValue)
          e.currentTarget.blur()
        }
      }}
      readOnly={readOnly}
      maxLength={config.maxLength || undefined}
      placeholder={config.placeholder ?? definition.name}
      className={compact ? COMPACT_EDITOR_CLASSES : FULL_EDITOR_CLASSES}
    />
  )
}

const NumberFieldEditor = ({ value, onChange, definition, compact, readOnly }: FieldEditorProps) => {
  const config = parseConfig(definition.config)
  const committedValue = value != null ? String(Number(value)) : ''
  const [draft, setDraft] = useState(committedValue)

  useEffect(() => {
    setDraft(committedValue)
  }, [committedValue])

  const commit = () => {
    if (draft === committedValue) return
    if (draft === '') {
      onChange(null)
      return
    }
    const n = Number(draft)
    if (Number.isNaN(n)) {
      setDraft(committedValue)
      return
    }
    onChange(n)
  }

  return (
    <input
      type="number"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          setDraft(committedValue)
          e.currentTarget.blur()
        }
      }}
      readOnly={readOnly}
      min={config.min}
      max={config.max}
      step={config.decimalPlaces != null ? Math.pow(10, -config.decimalPlaces) : undefined}
      placeholder={config.placeholder ?? definition.name}
      className={compact ? COMPACT_EDITOR_CLASSES : FULL_EDITOR_CLASSES}
    />
  )
}

const DateFieldEditor = ({ value, onChange, compact }: FieldEditorProps) => {
  const dateStr = value != null
    ? new Date(Number(value)).toISOString().split('T')[0]
    : ''
  return (
    <input
      type="date"
      value={dateStr}
      onChange={(e) => {
        const v = e.target.value
        onChange(v ? new Date(v).getTime() : null)
      }}
      className={compact ? COMPACT_EDITOR_CLASSES : FULL_EDITOR_CLASSES}
    />
  )
}

const ListFieldEditor = ({ value, onChange, options, compact }: FieldEditorProps) => {
  return (
    <select
      value={value != null ? String(value) : ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={compact ? COMPACT_EDITOR_CLASSES : FULL_EDITOR_CLASSES}
    >
      <option value="">—</option>
      {options.map((opt) => (
        <option key={opt.id} value={opt.id}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

// Compact cell renderers for list view
const StringCell = ({ value }: FieldCellProps) => (
  <span className="truncate text-xs text-muted-foreground">
    {value != null ? String(value) : ''}
  </span>
)

const NumberCell = ({ value }: FieldCellProps) => (
  <span className="truncate text-xs text-muted-foreground tabular-nums">
    {value != null ? String(value) : ''}
  </span>
)

const DateCell = ({ value }: FieldCellProps) => {
  const { date: fmtDate } = useDateTimeFormatters()
  return (
    <span className="truncate text-xs text-muted-foreground">
      {value != null ? fmtDate(new Date(Number(value))) : ''}
    </span>
  )
}

const ListCell = ({ value, options }: FieldCellProps) => {
  const option = options.find((o) => o.id === String(value))
  if (!option) return null
  return (
    <span className="inline-flex items-center gap-1 truncate text-xs">
      {option.color && (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: option.color }}
        />
      )}
      <span className="truncate text-muted-foreground">{option.label}</span>
    </span>
  )
}

// Format functions
function formatString(value: string | number | null): string {
  return value != null ? String(value) : ''
}

function formatNumber(value: string | number | null): string {
  return value != null ? String(value) : ''
}

function formatDate(value: string | number | null): string {
  // Note: formatValue is invoked outside React (e.g. CSV export), so locale
  // preferences can't be read here. UI surfaces should prefer DateCell which
  // uses useDateTimeFormatters.
  return value != null ? new Date(Number(value)).toLocaleDateString() : ''
}

function formatList(
  value: string | number | null,
  _def: CustomFieldDefinitionDocument,
  options: Array<CustomFieldOption>,
): string {
  if (value == null) return ''
  const option = options.find((o) => o.id === String(value))
  return option?.label ?? ''
}

// Validation functions
function validateString(value: string | number | null, config: any): string | null {
  if (value == null) return null
  const str = String(value)
  if (config?.maxLength && str.length > config.maxLength) {
    return `Maximum ${config.maxLength} characters`
  }
  return null
}

function validateNumber(value: string | number | null, config: any): string | null {
  if (value == null) return null
  const num = Number(value)
  if (Number.isNaN(num)) return 'Must be a number'
  if (config?.min != null && num < config.min) return `Minimum value is ${config.min}`
  if (config?.max != null && num > config.max) return `Maximum value is ${config.max}`
  return null
}

function validateDate(_value: string | number | null): string | null {
  return null
}

function validateList(_value: string | number | null): string | null {
  return null
}

/**
 * Central registry mapping field types to their UI components and utilities.
 * Adding a new custom field = zero code changes, just a new DB row.
 */
export const FIELD_TYPE_REGISTRY: Record<CustomFieldType, FieldTypeRegistryEntry> = {
  string: {
    Editor: StringFieldEditor,
    CellRenderer: StringCell,
    formatValue: formatString,
    validateValue: validateString,
  },
  number: {
    Editor: NumberFieldEditor,
    CellRenderer: NumberCell,
    formatValue: formatNumber,
    validateValue: validateNumber,
  },
  date: {
    Editor: DateFieldEditor,
    CellRenderer: DateCell,
    formatValue: formatDate,
    validateValue: validateDate,
  },
  list: {
    Editor: ListFieldEditor,
    CellRenderer: ListCell,
    formatValue: formatList,
    validateValue: validateList,
  },
}
